import { makeAutoObservable, observable, runInAction } from 'mobx';

import { clubOrigin } from '../domain/clubEntry';
import {
  asPurchaseError,
  type CardFormReply,
  MEMBERSHIP_PATH,
  type MembershipCardDetails,
  type MembershipPlanRef,
  type MembershipPurchaseFailure,
  type MembershipPurchaseResult,
  type MembershipPurchaseSucceededPayload,
  type MembershipWelcome,
  toCardFormReply,
  toPlanRef,
  toWelcome,
} from '../domain/purchase';
import type { Membership } from '../domain/types';
import {
  type CardForm,
  type CardFormHandlers,
  CardFormLoadError,
  type CardPaymentData,
} from '../payment/CardForm';
import {
  confirmErrorOutcome,
  formRuntimeIncident,
  formStartIncident,
  type PurchaseErrorOutcome,
  purchaseErrorOutcome,
  reconcileOutcome,
} from './purchaseOutcome';
import {
  BUSY_PHASES,
  type MembershipPurchaseExit,
  type MembershipPurchasePhase,
  type MembershipPurchaseStoreDeps,
} from './purchaseTypes';

const FAILED: CardFormReply = { kind: 'failed' };
const IGNORED: CardFormReply = { kind: 'ignored' };

/**
 * The purchase of one plan in the Join modal (`?modal=club-join`). Purchase and confirmation requests are sent
 * exactly once: a second click, an Enter in a card field or a repeated SDK
 * callback is ignored while one is in flight, and an unknown outcome is
 * reconciled through `/me` instead of being re-sent. After a decline or a
 * failed confirmation the card form is recreated, so another card can be
 * tried at once. `reset()` drops every answer still in flight.
 */
export class MembershipPurchaseStore {
  phase: MembershipPurchasePhase = 'idle';
  failure: MembershipPurchaseFailure | null = null;
  exit: MembershipPurchaseExit | null = null;
  consent = false;
  isCardValid = false;
  /** Changes whenever the card form must be recreated; the UI keys the form slot by it. */
  formKey = 0;
  plan: MembershipPlanRef | null = null;
  zoneId: string | null = null;
  /** Page the purchase started on, where "Continue shopping" returns after a bank redirect (`clubOrigin`). */
  origin: string = MEMBERSHIP_PATH;
  /** What Welcome states once the purchase settled as a member; `null` otherwise. */
  welcome: MembershipWelcome | null = null;

  private form: CardForm | null = null;
  private inFlight = false;
  private epoch = 0;
  private isChallengeTracked = false;
  /** The card form's code never arrived, so "Try again" has to reload the page. */
  private isReloadRequired = false;

  constructor(private readonly deps: MembershipPurchaseStoreDeps) {
    makeAutoObservable<
      MembershipPurchaseStore,
      | 'deps'
      | 'form'
      | 'inFlight'
      | 'epoch'
      | 'isChallengeTracked'
      | 'isReloadRequired'
    >(
      this,
      {
        plan: observable.ref,
        welcome: observable.ref,
        deps: false,
        form: false,
        inFlight: false,
        epoch: false,
        isChallengeTracked: false,
        isReloadRequired: false,
      },
      { autoBind: true },
    );
  }

  /** A request is in flight or its outcome is being checked. */
  get isBusy(): boolean {
    return BUSY_PHASES.has(this.phase);
  }

  /** The consent is ticked, the card is valid and nothing is in flight. */
  get canSubmit(): boolean {
    return this.phase === 'form' && this.consent && this.isCardValid;
  }

  /**
   * Opens the purchase of `plan`, remembering the page it started on (the
   * latest call's page, even for the plan and zone already open); otherwise a
   * no-op for the plan and zone already open or while a request is in flight.
   */
  start(plan: MembershipPlanRef, zoneId: string, origin: string): void {
    if (this.inFlight || this.exit !== null) {
      return;
    }
    this.origin = clubOrigin(origin);
    const isSamePlan = this.plan?.id === plan.id;
    if (isSamePlan && this.zoneId === zoneId && this.phase !== 'idle') {
      return;
    }
    this.plan = toPlanRef(plan);
    this.zoneId = zoneId;
    this.phase = 'form';
    if (!isSamePlan) {
      this.failure = null;
      this.consent = false;
    }
  }

  setConsent(value: boolean): void {
    this.consent = value;
  }

  /** Mounts a fresh card form into `node`, replacing the previous one. */
  async mountForm(node: HTMLElement): Promise<void> {
    this.unmountForm();
    const form = this.deps.createCardForm();
    this.form = form;
    try {
      await form.mount(node, this.handlersFor(form));
    } catch (error) {
      if (this.form !== form) {
        return;
      }
      runInAction(() => {
        this.failure = 'form_unavailable';
        this.isReloadRequired = error instanceof CardFormLoadError;
      });
      this.deps.report(formStartIncident(error));
      this.trackFailure('form_unavailable', this.plan);
    }
  }

  /** The form slot unmounted: a 3DS challenge shown in it is lost, so it ends on "not confirmed" with a fresh form. */
  releaseForm(): void {
    if (this.phase === 'challenge' && !this.inFlight && this.plan) {
      this.fail('not_confirmed', this.plan);
      return;
    }
    this.unmountForm();
  }

  unmountForm(): void {
    const form = this.form;
    this.form = null;
    this.isCardValid = false;
    form?.destroy();
  }

  /** "Try again" after the card form could not start; reloads the page when its code never arrived. */
  retryForm(): void {
    if (this.isReloadRequired) {
      this.deps.reloadPage();
      return;
    }
    this.failure = null;
    this.renewForm();
  }

  submit(): void {
    if (this.canSubmit) {
      this.form?.submit();
    }
  }

  /**
   * Continues a purchase after the bank's redirect: confirms the one-time
   * `redirectResult` once, and only for a context this customer saved for
   * this plan within the last 30 minutes.
   */
  async resumeRedirect(planId: string, redirectResult: string): Promise<void> {
    const context = this.deps.markers.readRedirect();
    this.deps.markers.clearRedirect();
    if (!context || context.plan.id !== planId || this.inFlight) {
      return;
    }
    this.plan = context.plan;
    this.zoneId = context.zoneId;
    this.origin = clubOrigin(context.origin ?? MEMBERSHIP_PATH);
    this.exit = null;
    const reply = await this.confirm({ redirectResult }, context.plan, null);
    if (reply.kind !== 'action') {
      return;
    }
    runInAction(() => {
      if (this.form) {
        this.form.handleAction(reply.action);
        return;
      }
      this.fail('not_confirmed', context.plan);
    });
  }

  /**
   * A bank return with no marker for it (expired, another browser, an
   * external browser opened from the app, a marker of another plan): the
   * one-time `redirectResult` is still confirmed, since confirm-card needs
   * neither a token nor a plan, and the purchase settles from the plan of the
   * answer's membership. Without a membership in the answer or with another
   * 3DS step `/me` is re-read instead: there is no plan to reopen the form
   * with. A failed request whose outcome is unknown settles through `/me`
   * like a marked one, so a `pending_card` that may still be charging leaves
   * for "Activating…" instead of offering a second payment. A refusal, a
   * further 3DS step or a purchase `/me` does not show leaves its failure on
   * Join, next to the plan offered again; so does a `/me` that failed, which
   * names no plan to wait for — the re-read `/me` decides what Join offers,
   * and a `pending_card` that may still be charging shows as "Activating…"
   * there (`isUnsettledPurchase`).
   */
  async resumeUnmarkedRedirect(redirectResult: string): Promise<void> {
    this.deps.markers.clearRedirect();
    if (this.inFlight) {
      return;
    }
    const epoch = this.epoch;
    this.inFlight = true;
    this.phase = 'confirming';
    this.failure = null;
    this.exit = null;
    let result: MembershipPurchaseResult | null = null;
    let unsettled: MembershipPurchaseFailure | null = null;
    let failed: MembershipPurchaseFailure | null = null;
    try {
      result = await this.deps.gateway.confirmCard({ redirectResult });
    } catch (error) {
      const outcome = confirmErrorOutcome(asPurchaseError(error));
      if (outcome.incident && epoch === this.epoch) {
        this.deps.report(outcome.incident);
      }
      if (outcome.next === 'reconcile') {
        unsettled = outcome.failure;
      } else {
        failed = outcome.failure;
      }
    }
    if (epoch !== this.epoch) {
      return;
    }
    if (unsettled !== null) {
      await this.reconcileUnmarked(epoch, unsettled);
      return;
    }
    runInAction(() => this.settleUnmarked(result, failed));
  }

  /** Plan of a bank redirect this customer is expected back from; `null` without a valid marker. */
  redirectPlanId(): string | null {
    return this.deps.markers.readRedirect()?.plan.id ?? null;
  }

  /**
   * Welcome is on screen: drops the welcome marker, so the club page shows no
   * banner for the same purchase, and returns what Welcome states. Without a
   * Welcome of its own the store leaves the marker for the club page.
   */
  takeWelcome(): MembershipWelcome | null {
    if (this.welcome === null) {
      return null;
    }
    this.deps.markers.takeWelcome();
    return this.welcome;
  }

  /** The Join modal (`?modal=club-join`) unmounted: drops the form; an idle purchase is forgotten, one in flight keeps settling. */
  leave(): void {
    this.unmountForm();
    if (!this.inFlight) {
      this.forget();
    }
  }

  /** Logout: drops answers in flight, the form and every purchase marker. */
  reset(): void {
    this.epoch += 1;
    this.inFlight = false;
    this.isChallengeTracked = false;
    this.unmountForm();
    this.forget();
    this.deps.markers.clearAll();
  }

  private forget(): void {
    this.phase = 'idle';
    this.failure = null;
    this.isReloadRequired = false;
    this.exit = null;
    this.plan = null;
    this.zoneId = null;
    this.origin = MEMBERSHIP_PATH;
    this.welcome = null;
    this.consent = false;
  }

  private handlersFor(form: CardForm): CardFormHandlers {
    return {
      onSubmit: (data) => this.handleSubmit(form, data),
      onAdditionalDetails: (details) => this.handleDetails(form, details),
      onValidityChange: (isValid) => this.setCardValidity(form, isValid),
      onActionShown: () => this.handleActionShown(form),
      onEnterKey: () => {
        if (this.form === form) {
          this.submit();
        }
      },
      onError: (error) => this.handleFormError(form, error),
    };
  }

  private setCardValidity(form: CardForm, isValid: boolean): void {
    if (this.form === form) {
      this.isCardValid = isValid;
    }
  }

  private async handleSubmit(
    form: CardForm,
    data: CardPaymentData,
  ): Promise<CardFormReply> {
    const { plan, zoneId } = this;
    if (
      form !== this.form ||
      this.inFlight ||
      !this.canSubmit ||
      !plan ||
      !zoneId
    ) {
      return IGNORED;
    }
    const epoch = this.epoch;
    this.inFlight = true;
    this.phase = 'submitting';
    this.failure = null;
    this.isChallengeTracked = false;
    this.deps.track({
      name: 'cardSubmitted',
      payload: { planCode: plan.code },
    });
    try {
      const result = await this.deps.gateway.purchase({
        planId: plan.id,
        zoneId,
        paymentMethod: data.paymentMethod,
        browserInfo: data.browserInfo,
        origin: data.origin,
        returnUrl: this.deps.returnUrl(),
      });
      return epoch === this.epoch ? this.settle(result, plan, form) : FAILED;
    } catch (error) {
      if (epoch === this.epoch) {
        await this.handleError(
          purchaseErrorOutcome(asPurchaseError(error)),
          plan,
          epoch,
        );
      }
      return FAILED;
    }
  }

  private handleDetails(
    form: CardForm,
    details: MembershipCardDetails,
  ): Promise<CardFormReply> {
    const { plan } = this;
    if (
      form !== this.form ||
      this.inFlight ||
      this.phase !== 'challenge' ||
      !plan
    ) {
      return Promise.resolve(IGNORED);
    }
    return this.confirm(details, plan, form);
  }

  /** `sender` is the form that asked for the confirmation, `null` for a bank return. */
  private async confirm(
    details: MembershipCardDetails,
    plan: MembershipPlanRef,
    sender: CardForm | null,
  ): Promise<CardFormReply> {
    const epoch = this.epoch;
    this.inFlight = true;
    this.phase = 'confirming';
    this.failure = null;
    try {
      const result = await this.deps.gateway.confirmCard(details);
      return epoch === this.epoch ? this.settle(result, plan, sender) : FAILED;
    } catch (error) {
      if (epoch === this.epoch) {
        await this.handleError(
          confirmErrorOutcome(asPurchaseError(error)),
          plan,
          epoch,
        );
      }
      return FAILED;
    }
  }

  /** An action goes only to the form that sent the request (any form for a bank return); a replaced form ends on "not confirmed". */
  private settle(
    result: MembershipPurchaseResult,
    plan: MembershipPlanRef,
    sender: CardForm | null,
  ): CardFormReply {
    this.inFlight = false;
    if (result.status === 'action_required') {
      if (!this.form || (sender !== null && sender !== this.form)) {
        this.fail('not_confirmed', plan);
        return FAILED;
      }
      this.phase = 'challenge';
      if (result.action?.type === 'redirect') {
        this.deps.markers.saveRedirect({
          plan: toPlanRef(plan),
          zoneId: this.zoneId,
          origin: this.origin,
        });
      }
      return toCardFormReply(result);
    }
    if (result.status === 'pending') {
      this.finishActivating(plan);
    } else {
      this.finishMember(result.status, result.membership, plan);
    }
    return toCardFormReply(result);
  }

  /** `failed` is the failure of a refused confirmation; `null` when it answered. */
  private settleUnmarked(
    result: MembershipPurchaseResult | null,
    failed: MembershipPurchaseFailure | null,
  ): void {
    this.inFlight = false;
    this.phase = 'idle';
    const membership = result?.membership ?? null;
    if (!result || !membership || result.status === 'action_required') {
      this.failure =
        failed ??
        (result?.status === 'action_required' ? 'not_confirmed' : null);
      this.deps.onMembershipChanged();
      return;
    }
    const plan = toPlanRef(membership.plan);
    this.plan = plan;
    if (result.status === 'pending') {
      this.finishActivating(plan);
      return;
    }
    this.finishMember(result.status, membership, plan);
  }

  /** An unknown outcome of an unmarked bank return: `/me` gives both the result and the plan; anything else only refreshes it. */
  private async reconcileUnmarked(
    epoch: number,
    otherwise: MembershipPurchaseFailure,
  ): Promise<void> {
    this.phase = 'reconciling';
    const membership = await this.deps.gateway.getMe().then(
      (answer) => answer,
      () => undefined,
    );
    if (epoch !== this.epoch) {
      return;
    }
    const outcome = reconcileOutcome(membership, otherwise);
    runInAction(() => {
      this.inFlight = false;
      this.phase = 'idle';
      if (outcome.kind === 'member') {
        const plan = toPlanRef(outcome.membership.plan);
        this.plan = plan;
        this.finishMember(outcome.status, outcome.membership, plan);
      } else if (outcome.kind === 'activating' && membership) {
        const plan = toPlanRef(membership.plan);
        this.plan = plan;
        this.finishActivating(plan);
      } else if (outcome.kind === 'unknown') {
        this.failure = 'payment_unavailable';
        this.deps.onMembershipChanged();
      } else {
        this.failure = otherwise;
        this.deps.onMembershipChanged();
      }
    });
  }

  private async handleError(
    outcome: PurchaseErrorOutcome,
    plan: MembershipPlanRef,
    epoch: number,
  ): Promise<void> {
    if (outcome.incident) {
      this.deps.report(outcome.incident);
    }
    if (outcome.refresh === 'membership') {
      this.deps.onMembershipChanged();
    }
    if (outcome.refresh === 'plans') {
      this.deps.onPlansStale();
    }
    if (outcome.next === 'reconcile') {
      await this.reconcile(plan, epoch, outcome.failure);
      return;
    }
    this.fail(outcome.failure, plan);
  }

  /**
   * The outcome is unknown: `/me` decides between success and `otherwise`;
   * never re-sends anything. A failed `/me` decides nothing and leaves for
   * the club page's activation wait (`finishUnknown`).
   */
  private async reconcile(
    plan: MembershipPlanRef,
    epoch: number,
    otherwise: MembershipPurchaseFailure,
  ): Promise<void> {
    this.inFlight = true;
    this.phase = 'reconciling';
    const membership = await this.deps.gateway.getMe().then(
      (answer) => answer,
      () => undefined,
    );
    if (epoch !== this.epoch) {
      return;
    }
    const outcome = reconcileOutcome(membership, otherwise);
    runInAction(() => {
      this.inFlight = false;
      if (outcome.kind === 'member') {
        this.finishMember(outcome.status, outcome.membership, plan);
      } else if (outcome.kind === 'activating') {
        this.finishActivating(plan);
      } else if (outcome.kind === 'unknown') {
        this.finishUnknown(plan);
      } else {
        this.fail(otherwise, plan);
      }
    });
  }

  private finishMember(
    status: 'trialing' | 'active',
    membership: Membership | null,
    plan: MembershipPlanRef,
  ): void {
    const welcome = toWelcome(status, membership, plan);
    this.welcome = welcome;
    this.deps.markers.saveWelcome(welcome);
    this.finish('member', {
      planCode: plan.code,
      status,
      trial: status === 'trialing',
    });
  }

  private finishActivating(plan: MembershipPlanRef): void {
    this.deps.markers.saveActivation({ planId: plan.id, planCode: plan.code });
    this.finish('activating', {
      planCode: plan.code,
      status: 'pending',
      trial: null,
    });
  }

  /**
   * `/me` failed while the outcome was unknown: leaves for the club page with
   * the activation marker, which polls `/me` for up to a minute and then
   * offers "Refresh", instead of a fresh card form and a second payment.
   * Not reported as a success: nothing says the purchase went through.
   */
  private finishUnknown(plan: MembershipPlanRef): void {
    this.deps.markers.saveActivation({ planId: plan.id, planCode: plan.code });
    this.finish('activating', null);
  }

  /** `payload` of `purchaseSucceeded`; `null` when the outcome is still unknown. */
  private finish(
    exit: MembershipPurchaseExit,
    payload: MembershipPurchaseSucceededPayload | null,
  ): void {
    this.phase = 'idle';
    this.failure = null;
    this.exit = exit;
    this.deps.markers.clearRedirect();
    if (payload) {
      this.deps.track({ name: 'purchaseSucceeded', payload });
    }
    this.deps.onMembershipChanged();
  }

  private fail(
    failure: MembershipPurchaseFailure,
    plan: MembershipPlanRef,
  ): void {
    this.failure = failure;
    this.trackFailure(failure, plan);
    this.renewForm();
  }

  private trackFailure(
    failure: MembershipPurchaseFailure,
    plan: MembershipPlanRef | null,
  ): void {
    if (plan) {
      this.deps.track({
        name: 'purchaseFailed',
        payload: { planCode: plan.code, reason: failure },
      });
    }
  }

  private renewForm(): void {
    this.unmountForm();
    this.inFlight = false;
    this.isChallengeTracked = false;
    this.phase = 'form';
    this.formKey += 1;
  }

  private handleActionShown(form: CardForm): void {
    if (form !== this.form || this.isChallengeTracked || !this.plan) {
      return;
    }
    this.isChallengeTracked = true;
    this.deps.track({
      name: 'threeDSShown',
      payload: { planCode: this.plan.code },
    });
  }

  private handleFormError(form: CardForm, error: unknown): void {
    if (form !== this.form || this.inFlight) {
      return;
    }
    this.deps.report(formRuntimeIncident(error));
    if (this.phase === 'challenge' && this.plan) {
      this.fail('not_confirmed', this.plan);
      return;
    }
    this.failure = 'form_unavailable';
    this.trackFailure('form_unavailable', this.plan);
  }
}
